import { useState } from "react";
import moment from "moment/moment";
import { useFormikContext } from "formik";
import Button from "../../../UI/Button";
import Callout from "../../../UI/Callout";
import Text from "../../../UI/Text";
// @ts-expect-error pure js
import { ReactSelect } from "../../../UI/Select";
// @ts-expect-error pure js
import { ReactSelectStandalone } from "../../../UI/SelectStandalone";
// @ts-expect-error pure js
import TextInputStandalone from "../../../UI/TextInputStandalone";
// @ts-expect-error pure js
import { useBalanceTypes } from "../../../../hooks/use-balances";
import {
  type FactureLigne,
  useFactureLines,
} from "../../../../hooks/use-facture-lines";
import {
  BalanceDirection,
  BalanceType,
  BalanceTypeCategory,
  RENT_CALL_TYPES,
} from "../../../../utils/constants/balances";
import { leaseKindOf } from "../../../../utils/constants/tenants";
// @ts-expect-error pure js
import useMandataire from "../../../../hooks/use-mandataire";
// @ts-expect-error pure js
import { currencyFormatter } from "../../../../api/Functions";
import type { Tenant } from "../../../../models/tenant";
import type {
  BalanceTypeCatalog,
  BalanceTypeDefinition,
} from "../../../../models/balance";
import styles from "./AvisEcheanceForm.module.css";
// @ts-expect-error pure js
import { classnames } from "../../../../utils";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import IconButton from "../../../UI/IconButton";

interface SelectOption<T = string | number> {
  label: string;
  value: T;
}

interface AvisEcheanceFormValues {
  periode?: string;
  lignes?: FactureLigne[];
}

const periodeOptions: SelectOption<string>[] = [
  { label: "Période en cours", value: "current" },
  { label: "Période suivante", value: "next" },
];

const tvaOptions: SelectOption<number>[] = [
  { label: "Non soumis", value: 0 },
  { label: "2.1%", value: 2.1 },
  { label: "5.5%", value: 5.5 },
  { label: "8.5%", value: 8.5 },
  { label: "10%", value: 10 },
  { label: "20%", value: 20 },
];

// Une opération est un crédit si son type est un avoir (avoir / remise, ou un appel en
// faveur du locataire d'après le catalogue) ou si son montant est saisi en négatif. Un
// crédit est compté en négatif dans le total ; on travaille sur la valeur absolue pour
// éviter tout double signe.
function isCreditLigne(
  ligne: FactureLigne,
  balanceTypes?: BalanceTypeCatalog,
): boolean {
  return (
    ligne.type === BalanceType.AVOIR_REMISE ||
    balanceTypes?.[ligne.type]?.direction === BalanceDirection.CREDIT ||
    ligne.montant_ht < 0
  );
}

function signedMontantTTC(
  ligne: FactureLigne,
  balanceTypes?: BalanceTypeCatalog,
): number {
  const magnitude = Math.abs(
    Math.round(ligne.montant_ht * (1 + ligne.taux_tva / 100) * 100) / 100,
  );
  return isCreditLigne(ligne, balanceTypes) ? -magnitude : magnitude;
}

interface LigneFactureProps {
  ligne: FactureLigne;
  index: number;
  onRemove: (index: number) => void;
  onUpdate: (index: number, ligne: FactureLigne) => void;
  typeOptions: SelectOption<string>[];
  balanceTypes?: BalanceTypeCatalog;
}

function LigneFacture({
  ligne,
  index,
  onRemove,
  onUpdate,
  typeOptions,
  balanceTypes,
}: LigneFactureProps) {
  // Champ montant contrôlé en texte pour les lignes éditables : un input number
  // contrôlé par un nombre écraserait un "-" saisi seul, empêchant la saisie
  // d'un montant négatif (avoir / remise).
  const [montantText, setMontantText] = useState<string>(
    ligne.montant_ht ? String(ligne.montant_ht) : "",
  );

  const montantTTC = signedMontantTTC(ligne, balanceTypes);

  const typeLabel = balanceTypes?.[ligne.type]?.text || ligne.type;
  // Le loyer et les charges ont leur libellé de période ; tout autre poste décrit son objet.
  const needsComment =
    !RENT_CALL_TYPES.includes(ligne.type) &&
    ligne.type !== BalanceType.APPEL_CHARGE;

  return (
    <div
      className={classnames(
        styles.ligne,
        ligne.existing && styles.ligneExisting,
        ligne.has_facture && styles.ligneHasFacture,
      )}
    >
      <div className={styles.row}>
        <div className={styles.colCategorie}>
          {ligne.existing ? (
            <span>{typeLabel}</span>
          ) : (
            <ReactSelectStandalone
              options={typeOptions}
              value={typeOptions.find((o) => o.value === ligne.type) || null}
              onChange={(option: SelectOption<string> | null) =>
                onUpdate(index, { ...ligne, type: option?.value || "" })
              }
              placeholder="Type"
            />
          )}
        </div>
        <div className={styles.colMontant}>
          <TextInputStandalone
            type="number"
            step="0.01"
            min=""
            value={ligne.existing ? ligne.montant_ht : montantText}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setMontantText(e.target.value);
              const parsed = parseFloat(e.target.value);
              onUpdate(index, {
                ...ligne,
                montant_ht: isNaN(parsed) ? 0 : parsed,
              });
            }}
            unit="€ HT"
            disabled={ligne.existing}
          />
        </div>
        <div className={styles.colTva}>
          {ligne.existing ? (
            <Text as="span">
              {ligne.taux_tva > 0 ? `${ligne.taux_tva}%` : "Non soumis"}
            </Text>
          ) : (
            <ReactSelectStandalone
              options={tvaOptions}
              value={
                tvaOptions.find((o) => o.value === ligne.taux_tva) ||
                tvaOptions[0]
              }
              onChange={(option: SelectOption<number> | null) =>
                onUpdate(index, {
                  ...ligne,
                  taux_tva: option?.value ?? 0,
                })
              }
              placeholder="TVA"
            />
          )}
        </div>
        <Text as="div" className={styles.montantTTC}>
          {currencyFormatter(montantTTC)} TTC
        </Text>
        {!ligne.existing && (
          <IconButton
            variant="plain"
            color="danger"
            label="Supprimer l'entrée"
            onClick={() => onRemove(index)}
            icon={IconTrash}
          />
        )}
      </div>
      {!ligne.existing && needsComment && (
        <TextInputStandalone
          value={ligne.label}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onUpdate(index, { ...ligne, label: e.target.value })
          }
          placeholder="Commentaire (ex: Ordures ménagères)"
        />
      )}
      {ligne.existing && ligne.label && (
        <Text variant="deci" color="muted">
          {ligne.label}
        </Text>
      )}
    </div>
  );
}

interface AvisEcheanceFormProps {
  tenant?: Tenant;
}

export default function AvisEcheanceForm({ tenant }: AvisEcheanceFormProps) {
  const { values, setFieldValue } = useFormikContext<AvisEcheanceFormValues>();
  const {
    balanceTypes,
    balanceTypesByCategory,
    isLoading: isLoadingBalanceTypes,
  } = useBalanceTypes();

  const { mandataire } = useMandataire();
  const leaseKind = leaseKindOf(tenant?.lease_type);
  const typeOptions: SelectOption<string>[] =
    isLoadingBalanceTypes || !balanceTypesByCategory
      ? []
      : [
          ...(
            (balanceTypesByCategory[
              BalanceTypeCategory.LOCATAIRE_APPEL
            ] as BalanceTypeDefinition[]) || []
          )
            .filter((bt) => bt.creation)
            .filter((bt) => mandataire || !bt.agency_only)
            .filter((bt) => !leaseKind || bt.lease_kinds.includes(leaseKind))
            .map((bt) => ({ label: bt.text, value: bt.static_id }))
            .sort((a, b) => a.label.localeCompare(b.label)),
          {
            label:
              balanceTypes?.[BalanceType.AVOIR_REMISE]?.text ||
              "Avoir / remise",
            value: BalanceType.AVOIR_REMISE,
          },
        ];

  const isFactureMode = !!tenant && !tenant.is_type_quittance;

  const {
    periodeStart,
    periodeEnd,
    isLoading: loading,
  } = useFactureLines(
    isFactureMode ? tenant?.id : null,
    isFactureMode ? values.periode : null,
  );

  const lignes: FactureLigne[] = values.lignes || [];

  function updateLigne(index: number, updatedLigne: FactureLigne) {
    const updated = [...lignes];
    updated[index] = updatedLigne;
    setFieldValue("lignes", updated);
  }

  function removeLigne(index: number) {
    setFieldValue(
      "lignes",
      lignes.filter((_, i) => i !== index),
    );
  }

  function addLigne() {
    setFieldValue("lignes", [
      ...lignes,
      {
        type: "appel_charge_exceptionnelle",
        label: "",
        montant_ht: 0,
        taux_tva: 0,
        existing: false,
        id_balance: null,
      },
    ]);
  }

  const hasNewLines = lignes.some((l) => !l.existing);
  const allHaveFacture =
    lignes.length > 0 && !hasNewLines && lignes.every((l) => l.has_facture);

  const totalTTC = lignes
    .filter((l) => !l.has_facture)
    .reduce((sum, l) => sum + signedMontantTTC(l, balanceTypes), 0);

  const formattedStart = periodeStart
    ? moment(periodeStart).format("DD/MM/YYYY")
    : null;
  const formattedEnd = periodeEnd
    ? moment(periodeEnd).format("DD/MM/YYYY")
    : null;

  return (
    <>
      <ReactSelect
        name="periode"
        label="Période"
        mandatory
        options={periodeOptions}
      />

      {isFactureMode && (
        <div className={styles.fields}>
          {formattedStart && formattedEnd && (
            <Text weight="medium">
              Période du {formattedStart} au {formattedEnd}
            </Text>
          )}

          <Text weight="bold">Lignes de la facture</Text>

          {loading && (
            <Text variant="deci" color="muted">
              Chargement...
            </Text>
          )}

          {!loading && lignes.length > 0 && (
            <>
              <div className={classnames(styles.row, styles.header)}>
                <Text
                  as="div"
                  variant="deci"
                  color="muted"
                  className={styles.colCategorie}
                >
                  Catégorie
                </Text>
                <Text
                  as="div"
                  variant="deci"
                  color="muted"
                  className={styles.colMontant}
                >
                  Montant HT
                </Text>
                <Text
                  as="div"
                  variant="deci"
                  color="muted"
                  className={styles.colTva}
                >
                  TVA
                </Text>
                <Text
                  as="div"
                  variant="deci"
                  color="muted"
                  className={styles.montantTTC}
                >
                  Montant TTC
                </Text>
              </div>
              {lignes.map((ligne, index) => (
                <LigneFacture
                  key={`${ligne.type}-${index}`}
                  ligne={ligne}
                  index={index}
                  onRemove={removeLigne}
                  onUpdate={updateLigne}
                  typeOptions={typeOptions}
                  balanceTypes={balanceTypes}
                />
              ))}

              {allHaveFacture && (
                <Callout variant="warning">
                  Une facture existe déjà pour toutes ces lignes. Ajoutez une
                  nouvelle opération si vous souhaitez générer une nouvelle
                  facture.
                </Callout>
              )}
              {!allHaveFacture && lignes.some((l) => l.existing) && (
                <Text variant="deci">
                  Montants à payer déjà existants dans la comptabilité repris
                  sur la facture
                </Text>
              )}

              <div className={styles.totalRow}>
                <Text as="span" weight="bold">
                  Total TTC
                </Text>
                <Text as="span" weight="bold">
                  {currencyFormatter(totalTTC)}
                </Text>
              </div>
            </>
          )}

          {!loading && lignes.length === 0 && values.periode && (
            <Callout variant="warning">
              Aucune ligne à facturer pour cette période.
            </Callout>
          )}

          <Button variant="secondary" onClick={addLigne} icon={IconPlus}>
            Ajouter une opération
          </Button>
          <Text variant="deci" color="muted">
            Pour une remise, choisissez la catégorie « Avoir / remise » ou
            saisissez un montant négatif.
          </Text>
        </div>
      )}
    </>
  );
}
